import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { Button, Icon, Text } from '@nimbus-ds/components';
import { CogIcon } from '@nimbus-ds/icons';
import { useParams } from 'react-router';
import { FEATURE_NE_PREMIUM_DELIVERY_OPTIONS } from 'App/features';
import { useNavegate } from 'App/hooks';
import { ErrorScreen } from 'commons/components';
import { useForm, useToastStatus } from 'commons/hooks';
import { NE_PREMIUM_DELIVERY_OPTIONS_SOURCE } from 'config/upsellFlowSources';
import { useHasShippingLimitations } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import useGetDetailActiveApp from './useGetDetailActiveApp';
import useUpdateDetailActiveApp from './useUpdateDetailActiveApp';
import { useGetShippingRestrictions } from '../../hooks';
import {
  DELIVERY_METHODS_ROUTES,
  SHIPPING_METHODS_BASE_ROUTE,
} from '../../shippingRoutes';
import { NE_PREMIUM_DELIVERY_OPTIONS } from '../components/ActiveShippingMethodsCard/utils/ne-premium-delivery-options';
import CarriersForm from '../components/CarriersForm';
import { createShippingModalityValidationSchema } from '../components/ShippingModalityCard';
import ShippingPage from '../components/ShippingPage';

function EditCarriersPage() {
  const t = useTranslationShipping();
  const { goTo, getQueryParam, goBack } = useNavegate();
  const { id } = useParams<{ id: string }>();
  const { detailActiveApp, fetchDetailActiveApp, isError, isLoading } =
    useGetDetailActiveApp();

  const { updateDetailActive, status } = useUpdateDetailActiveApp();
  const { restrictions, fetchRestrictions } = useGetShippingRestrictions();
  const hasShippingLimitations = useHasShippingLimitations();

  const modalityCodes = useMemo(
    () => Object.keys(detailActiveApp?.options ?? {}),
    [detailActiveApp?.options],
  );
  const validationSchema = useMemo(
    () => createShippingModalityValidationSchema(modalityCodes),
    [modalityCodes],
  );

  const { values, handleOnSubmit, handleChangeGroup, errors } = useForm({
    initialValues: detailActiveApp?.options ?? {},
    validationSchema,
    onSubmit: async (data) => {
      if (data) {
        await updateDetailActive(id, data);
        const fromOnboarding = getQueryParam('from') === 'onBoarding';

        if (fromOnboarding) {
          goTo(SHIPPING_METHODS_BASE_ROUTE);
        } else {
          goBack();
        }
      }
    },
  });

  const transformedErrors = Object.keys(errors || {}).reduce((acc, key) => {
    if (key.includes('.') && errors[key]) {
      const [modalityCode, field] = key.split('.');
      if (!acc[modalityCode]) {
        acc[modalityCode] = {};
      }
      const errorMessage = errors[key];
      if (errorMessage) {
        acc[modalityCode][field] = errorMessage;
      }
    }
    return acc;
  }, {} as Record<string, Record<string, string>>);

  useToastStatus({
    status,
    success: t('carriers.successToast'),
    error: t('carriers.errorToast'),
  });

  const handleOnFetchDetailActiveApp = useCallback(() => {
    fetchDetailActiveApp(id);
  }, [fetchDetailActiveApp, id]);

  useEffect(() => {
    handleOnFetchDetailActiveApp();
    fetchRestrictions(id);
  }, [handleOnFetchDetailActiveApp, fetchRestrictions, id]);

  const triggerUpsellFlow = useUpsellFlow({
    featureKey: FEATURE_NE_PREMIUM_DELIVERY_OPTIONS,
    trackingSource: NE_PREMIUM_DELIVERY_OPTIONS_SOURCE,
    title: t(
      'deliveryMethods.activeShippingMethods.editPage.modalitiesCard.nePremiumDeliveryOptions.upsellTitle',
    ),
    callback: (group: string, changeEvent: ChangeEvent<HTMLInputElement>) => {
      handleChangeGroup(group, changeEvent);
    },
  });

  const handleChangeGroupWithUpsell = useCallback(
    (
      deliveryOptionCode: string,
      changeEvent: ChangeEvent<HTMLInputElement>,
    ) => {
      const isTryingToEnableDeliveryOption =
        changeEvent.target.name === 'isEnabled' && changeEvent.target.checked;
      const isNePremiumDeliveryOption =
        NE_PREMIUM_DELIVERY_OPTIONS.includes(deliveryOptionCode);
      const shouldUseUpsellFlow =
        isTryingToEnableDeliveryOption && isNePremiumDeliveryOption;
      if (shouldUseUpsellFlow) {
        triggerUpsellFlow(deliveryOptionCode, changeEvent);
      } else {
        handleChangeGroup(deliveryOptionCode, changeEvent);
      }
    },
    [handleChangeGroup, triggerUpsellFlow],
  );

  const handleNavigateToRestrictions = useCallback(() => {
    const hasRestrictions = restrictions && restrictions.length > 0;

    if (hasRestrictions) {
      goTo(DELIVERY_METHODS_ROUTES.carrierRestrictions.replace(':id', id));
    } else {
      goTo(DELIVERY_METHODS_ROUTES.newCarrierRestriction.replace(':id', id));
    }
  }, [goTo, id, restrictions]);

  return (
    <ShippingPage
      isError={isError}
      handleSave={handleOnSubmit}
      title={isLoading ? 'skeleton' : detailActiveApp?.name ?? ''}
      isLoading={isLoading}
      isUpdating={status === 'loading'}
      actions={
        !isLoading && !isError && hasShippingLimitations ? (
          <Button onClick={handleNavigateToRestrictions}>
            <Icon color="neutral-textHigh" source={<CogIcon />} />
            <Text color="neutral-textHigh">
              {t('deliveryMethods.configureRestriction')}
            </Text>
          </Button>
        ) : undefined
      }
    >
      {isError && (
        <ErrorScreen
          description={t('genericError.text')}
          onRetry={handleOnFetchDetailActiveApp}
        />
      )}
      {isLoading && <CarriersForm.Skeleton />}
      {detailActiveApp && !isLoading && !isError && (
        <CarriersForm
          values={values}
          handleOnChangeGroup={handleChangeGroupWithUpsell}
          isCompatibleWithMultiCD={detailActiveApp.isMultiCDCompatible}
          errors={transformedErrors}
        />
      )}
    </ShippingPage>
  );
}

export default EditCarriersPage;
